import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { fetchAllVersions } from '../../../../../api/businessAndEnterprise/sheetApi';
import DrawerMenu from './DrawerMenu';
import { formatDateTime } from '../../../../../utils/helper';
import Context from '../../../../../context/Context';
import InfiniteScroll from '../../../../common/infiniteScroll/InfiniteScroll';

const VersionHistoryDrawer = ({ show, onHide, sheetId, handleEditSheet }) => {
    const [openDropdown, setOpenDropdown] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [versions, setVersions] = useState([]);
    const [versionsTotalCount, setVersionsTotalCount] = useState(null);
    const [page, setPage] = useState(1);
    const loaderRef = useRef(null);
    const { currentSheetIndex, allComments } = useContext(Context);

    const toggleDropdown = (id) => {
        setOpenDropdown(openDropdown === id ? null : id);
    };

    const getAllVersions = useCallback((pageNum = 1) => {
        setLoading(true);
        fetchAllVersions(sheetId, { page: pageNum }, (response) => {
            if (response?.status === true) {
                setVersions(prev => pageNum === 1 ? (response?.data || []) : [...prev, ...(response?.data || [])]);
                setVersionsTotalCount(response?.count);
            }
            setLoading(false);
            setHasFetched(true);
        });
    }, [sheetId]);

    useEffect(() => {
        if (show) {
            setPage(1);
            getAllVersions(1);
            setHasFetched(true);
        }
    }, [show, currentSheetIndex, allComments, getAllVersions]);

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            className="!w-[300px]"
        >
            <Offcanvas.Header className="border-b border-gray-200 pb-2">
                <div className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold text-gray-900">Version history</h2>
                    <button
                        onClick={onHide}
                        className="w-8 h-8 mb-1 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                    >
                        <svg className="w-6 !h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </Offcanvas.Header>

            <Offcanvas.Body className="p-0">
                <div className="flex flex-col">
                    {/* Current Version - Always shown first */}
                    {versions?.length > 0 && (
                        <div className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors">
                            <div className="flex-shrink-0 mt-1">
                                <div className="w-[20px] h-[20px] rounded-full bg-[#AEF468]"></div>
                            </div>

                            <div className="flex-1 min-w-0">
                                <span className="text-sm font-semibold text-black">
                                    Current version
                                </span>
                                <div className="text-sm font-medium text-gray-900">
                                    {formatDateTime(new Date())}
                                </div>
                            </div>

                            <div className="flex-shrink-0 relative">
                                <button
                                    onClick={() => toggleDropdown('current')}
                                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                                >
                                    <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                    </svg>
                                </button>

                                {openDropdown === 'current' && (
                                    <DrawerMenu
                                        version={{ id: 'current' }}
                                        sheetId={sheetId}
                                        versionId="current"
                                        setOpenDropdown={setOpenDropdown}
                                        onHide={onHide}
                                        getAllVersions={getAllVersions}
                                        isCurrentVersion={true}
                                        handleEditSheet={handleEditSheet}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Version History with Infinite Scroll */}
                    {versions?.length > 0 ? (
                        <InfiniteScroll
                            onLoadMore={getAllVersions}
                            page={page}
                            setPage={setPage}
                            list={versions}
                            listTotalCount={versionsTotalCount}
                            loading={loading}
                            loaderRef={loaderRef}
                        >
                            {versions?.map((version) => (
                                <div
                                    key={version?.id}
                                    className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors"
                                >
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="w-[20px] h-[20px] rounded-full bg-gray-300"></div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900">
                                            {formatDateTime(version?.created_at)}
                                        </div>
                                        {version?.restored_from_version_date !== null && (
                                            <div className="text-xs text-gray-600 mt-0.5">
                                                Restored from <span className='font-bold text-gray-800'>{formatDateTime(version?.restored_from_version_date)}</span>
                                            </div>
                                        )}
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            Saved by {version?.user?.name}
                                        </div>
                                    </div>

                                    <div className="flex-shrink-0 relative">
                                        <button
                                            onClick={() => toggleDropdown(version?.id)}
                                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                                        >
                                            <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                            </svg>
                                        </button>

                                        {openDropdown === version?.id && (
                                            <DrawerMenu
                                                version={version}
                                                sheetId={sheetId}
                                                versionId={version?.id}
                                                setOpenDropdown={setOpenDropdown}
                                                onHide={onHide}
                                                getAllVersions={getAllVersions}
                                                handleEditSheet={handleEditSheet}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </InfiniteScroll>
                    ) : (
                        !loading && hasFetched && (
                            <div className="flex flex-col items-center justify-center py-12 px-4">
                                <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-gray-500 text-center">No versions yet</p>
                            </div>
                        )
                    )}
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default VersionHistoryDrawer;